import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { clothingAPI, proxyClothingImageUrl, cheeseAPI, adminItemsAPI } from '../api/client';
import { useAuthStore } from '../store';
import '../styles/product-modal.css';

const SWIPE_THRESHOLD = 50;

const formatPrice = (raw) => {
  if (!raw) return null;
  const num = parseFloat(String(raw).replace(/\s/g, '').replace(',', '.'));
  if (isNaN(num)) return raw;
  return Number.isInteger(num)
    ? num.toLocaleString('ru-RU')
    : num.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const SupplierLink = ({ href, label }) => {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="supplier-link-btn">
      {label}
    </a>
  );
};

const SkuValue = ({ sku }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(sku).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 600);
    }).catch(() => {});
  };
  return (
    <span className={`detail-value sku-value${copied ? ' sku-value--copied' : ''}`} onClick={handle}>
      {sku}
      <span className="sku-copied-hint">{copied ? 'Скопировано!' : 'Копировать'}</span>
    </span>
  );
};

const ClothingSuppliersSection = ({ sku, onZoom, onSupplierZoom, withBust: wb }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const isAdmin = useAuthStore(s => s.user?.is_admin);

  useEffect(() => {
    if (!sku) return;
    setLoading(true);
    clothingAPI.getSuppliers(sku)
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [sku]);

  if (loading) {
    return (
      <div className="suppliers-section">
        <div className="suppliers-loading">Загрузка наличия...</div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="suppliers-section">
        <p className="suppliers-empty">Нет поставщиков</p>
      </div>
    );
  }

  const displayed = [...items].sort((a, b) => {
    if (sortBy === 'price_asc') return (a.price_numeric || 0) - (b.price_numeric || 0);
    if (sortBy === 'price_desc') return (b.price_numeric || 0) - (a.price_numeric || 0);
    return 0;
  });

  const cyclePriceSort = () =>
    setSortBy(cur => cur === 'price_asc' ? 'price_desc' : cur === 'price_desc' ? 'default' : 'price_asc');

  return (
    <div className="suppliers-section">
      <div className="suppliers-header">
        <span className="suppliers-title">
          Наличие у поставщиков
          <span className="suppliers-count">{items.length}</span>
        </span>
        <button
          className={`suppliers-price-sort${sortBy !== 'default' ? ' suppliers-price-sort--active' : ''}`}
          onClick={cyclePriceSort}
        >
          Цена{sortBy === 'price_asc' ? ' ↑' : sortBy === 'price_desc' ? ' ↓' : ' ↕'}
        </button>
      </div>

      <div className="suppliers-list">
        {(() => {
          const photoSuppliers = displayed.filter(x => x.supplier_photo_url);
          return displayed.map((s, i) => {
          const sizes = s.sizes ? s.sizes.split(',').map(x => x.trim()).filter(Boolean) : [];
          return (
            <div key={s.supplier_code || i} className="supplier-card">
              <div className="supplier-card-top">
                {s.supplier_photo_url && (
                  <img
                    src={wb ? wb(s.supplier_photo_url) : proxyClothingImageUrl(s.supplier_photo_url)}
                    alt=""
                    className="supplier-photo supplier-photo--zoomable"
                    loading="lazy"
                    onClick={() => {
                      if (onSupplierZoom) {
                        const idx = photoSuppliers.findIndex(x => (x.supplier_code || x.supplier_name) === (s.supplier_code || s.supplier_name));
                        onSupplierZoom({ suppliers: photoSuppliers, idx: idx >= 0 ? idx : 0 });
                      } else {
                        onZoom && onZoom(s.supplier_photo_url);
                      }
                    }}
                  />
                )}
                <div className="supplier-card-info">
                  <div className="supplier-header">
                    <span className="supplier-name">
                      {s.supplier_name || '—'}
                      {isAdmin && s.supplier_code && (
                        <span className="supplier-code-admin">{s.supplier_code}</span>
                      )}
                    </span>
                    <div className="supplier-header-right">
                      {s.photo_link && (
                        <a href={s.photo_link} target="_blank" rel="noopener noreferrer" className="supplier-crm-btn">
                          CRM
                        </a>
                      )}
                      {s.price && <span className="supplier-price">{formatPrice(s.price)} ₴</span>}
                    </div>
                  </div>

                  {sizes.length > 0 && (
                    <div className="supplier-sizes">
                      {sizes.map(sz => (
                        <span key={sz} className="supplier-size-chip">{sz}</span>
                      ))}
                    </div>
                  )}

                  {s.material && <div className="supplier-grid">{s.material}</div>}

                  <div className="supplier-links">
                    <SupplierLink href={s.supplier_link} label="Поставщик" />
                    <SupplierLink href={s.photo_post_link} label="Пост с фото" />
                  </div>
                </div>
              </div>
            </div>
          );
        });
        })()}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="detail-row">
      <span className="detail-label">{label}:</span>
      <span className="detail-value">{value}</span>
    </div>
  );
};

const ClothingProductModal = ({ product, onClose, onPrev, onNext }) => {
  const [zoom, setZoom] = useState(null);
  const [supplierZoom, setSupplierZoom] = useState(null); // { suppliers: [], idx: 0 }
  const [details, setDetails] = useState(product);
  const isAdmin = useAuthStore(s => s.user?.is_admin);
  const [dir, setDir] = useState(null);

  const [rotatingUrl, setRotatingUrl] = useState(null);
  const [photoTimestamps, setPhotoTimestamps] = useState(() => {
    try {
      const raw = localStorage.getItem('alldrop_photoTs');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      return Object.fromEntries(Object.entries(parsed).filter(([, ts]) => ts > cutoff));
    } catch { return {}; }
  });
  const _OUR_ORIGIN = 'https://alldrop.ink/images';
  const isOurImage = (url) => typeof url === 'string' && url.startsWith(_OUR_ORIGIN);
  const withBust = (url) => {
    if (!url) return url;
    const base = url.split('?')[0];
    const ts = photoTimestamps[base];
    return ts ? `${base}?t=${ts}` : url;
  };
  const handleRotate = async (url, degrees) => {
    if (!url || rotatingUrl) return;
    const base = url.split('?')[0];
    setRotatingUrl(base);
    try {
      await adminItemsAPI.rotatePhoto(base, degrees);
      const ts = Date.now();
      setPhotoTimestamps(prev => {
        const next = { ...prev, [base]: ts };
        try { localStorage.setItem('alldrop_photoTs', JSON.stringify(next)); window.dispatchEvent(new Event('photo-rotated')); } catch {}
        return next;
      });
    } catch (err) {
      alert(`Ошибка поворота: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setRotatingUrl(null);
    }
  };

  const handleRevert = async (url) => {
    if (!url || rotatingUrl) return;
    const base = url.split('?')[0];
    setRotatingUrl(base);
    try {
      await adminItemsAPI.revertPhoto(base);
      const ts = Date.now();
      setPhotoTimestamps(prev => {
        const next = { ...prev, [base]: ts };
        try { localStorage.setItem('alldrop_photoTs', JSON.stringify(next)); window.dispatchEvent(new Event('photo-rotated')); } catch {}
        return next;
      });
    } catch (err) {
      alert(`Ошибка восстановления: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setRotatingUrl(null);
    }
  };
  const [addingToOrder, setAddingToOrder] = useState(false);
  const [orderAdded, setOrderAdded] = useState(false);
  const prevRef = useRef(product);

  useEffect(() => {
    // Подгружаем полные поля товара по sku
    setDetails(product);
    if (product?.sku) {
      clothingAPI.getBySku(product.sku)
        .then(r => { if (r.data) setDetails(r.data); })
        .catch(() => {});
    }
  }, [product]);

  useEffect(() => {
    if (prevRef.current !== product) {
      prevRef.current = product;
      const t = setTimeout(() => setDir(null), 200);
      return () => clearTimeout(t);
    }
  }, [product]);

  const navigate = (fn, direction) => {
    if (!fn) return;
    setDir(direction);
    fn();
  };

  useEffect(() => {
    const handler = (e) => {
      if (supplierZoom) {
        if (e.key === 'Escape') { setSupplierZoom(null); return; }
        if (e.key === 'ArrowLeft') {
          setSupplierZoom(z => z && z.idx > 0 ? { ...z, idx: z.idx - 1 } : z);
          return;
        }
        if (e.key === 'ArrowRight') {
          setSupplierZoom(z => z && z.idx < z.suppliers.length - 1 ? { ...z, idx: z.idx + 1 } : z);
          return;
        }
        return;
      }
      if (e.key === 'Escape') { if (zoom) { setZoom(null); } else { onClose(); } return; }
      if (zoom) return;
      if (e.key === 'ArrowLeft') navigate(onPrev, 'right');
      if (e.key === 'ArrowRight') navigate(onNext, 'left');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onPrev, onNext, onClose, zoom, supplierZoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const touchStartX = useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx > SWIPE_THRESHOLD) navigate(onPrev, 'right');
    if (dx < -SWIPE_THRESHOLD) navigate(onNext, 'left');
  };

  const d = details;

  return (
    <div className="modal-overlay" onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="modal-wrapper" onClick={e => e.stopPropagation()}>

        <button
          className={`modal-nav modal-nav-prev${!onPrev ? ' modal-nav-disabled' : ''}`}
          onClick={() => navigate(onPrev, 'right')}
          disabled={!onPrev}
          title="Предыдущий (←)"
        >‹</button>

        <div className={`modal modal-navigable${dir ? ` modal-slide-${dir}` : ''}`}>
          <div className="modal-header">
            <div>
              <h2>{d.name}</h2>
              {d.brand && <p className="modal-subtitle">{d.brand}</p>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="close-btn" onClick={onClose}>×</button>
            </div>
          </div>

          <div className="modal-body">
            {d.photo_url && (
              <div className="modal-image" onClick={() => setZoom(d.photo_url)}>
                <img src={withBust(d.photo_url)} alt={d.name} referrerPolicy="no-referrer" />
                {isAdmin && isOurImage(d.photo_url) && (
                  <div className="photo-rotate-bar">
                    <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                      onClick={e => { e.stopPropagation(); handleRotate(d.photo_url, -90); }} title="Повернуть против часовой">↺</button>
                    <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                      onClick={e => { e.stopPropagation(); handleRotate(d.photo_url, 90); }} title="Повернуть по часовой">↻</button>
                    <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                      onClick={e => { e.stopPropagation(); handleRevert(d.photo_url); }} title="Восстановить оригинал">↩</button>
                  </div>
                )}
              </div>
            )}

            <div className="product-details">
              <div className="detail-row">
                <span className="detail-label">Артикул:</span>
                <SkuValue sku={d.sku} />
              </div>
              <DetailRow label="Тип" value={d.product_type} />
              <DetailRow label="Бренд" value={d.brand} />
              <DetailRow label="Цвет" value={d.color} />
              <DetailRow label="Материал" value={d.material} />
              <DetailRow label="Пол" value={d.gender} />
              <DetailRow label="Сезон" value={d.season} />
              <DetailRow label="Замеры" value={d.measurements} />
              <DetailRow label="Наличие" value={d.availability} />
              {d.description && (
                <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="detail-label">Описание:</span>
                  <span className="detail-value" style={{ whiteSpace: 'pre-wrap' }}>{d.description}</span>
                </div>
              )}
            </div>

            <ClothingSuppliersSection sku={d.sku} onZoom={setZoom} onSupplierZoom={setSupplierZoom} withBust={withBust} />
          </div>

          <div className="modal-footer">
            {isAdmin && (
              <button
                className="btn btn-secondary"
                style={{ marginRight: 'auto', color: orderAdded ? '#4ade80' : undefined }}
                disabled={addingToOrder}
                onClick={async () => {
                  setAddingToOrder(true);
                  try {
                    await cheeseAPI.createOrder({
                      name: details.name || product.name,
                      photo_url: details.photo_url || product.photo_url || null,
                      sizes_qty: {},
                      has_box: false,
                    });
                    setOrderAdded(true);
                    setTimeout(() => setOrderAdded(false), 2500);
                  } catch {
                    alert('Ошибка добавления в закупку');
                  } finally {
                    setAddingToOrder(false);
                  }
                }}
              >
                {orderAdded ? '✓ Добавлено' : addingToOrder ? '…' : 'В закупку'}
              </button>
            )}
            <button className="btn btn-secondary" onClick={onClose}>Закрыть</button>
          </div>
        </div>

        <button
          className={`modal-nav modal-nav-next${!onNext ? ' modal-nav-disabled' : ''}`}
          onClick={() => navigate(onNext, 'left')}
          disabled={!onNext}
          title="Следующий (→)"
        >›</button>

      </div>

      {zoom && ReactDOM.createPortal(
        <div className="photo-lightbox" onClick={() => setZoom(null)}>
          <img src={withBust(zoom)} alt="" referrerPolicy="no-referrer" onClick={e => e.stopPropagation()} />
          <button className="lightbox-close" onClick={() => setZoom(null)}>×</button>
          {isAdmin && isOurImage(zoom) && (
            <div className="photo-rotate-bar" style={{position:'fixed', bottom:60, left:'50%', transform:'translateX(-50%)', zIndex:2001}}>
              <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                onClick={e => { e.stopPropagation(); handleRotate(zoom, -90); }} title="Повернуть против часовой">↺</button>
              <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                onClick={e => { e.stopPropagation(); handleRotate(zoom, 90); }} title="Повернуть по часовой">↻</button>
              <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                onClick={e => { e.stopPropagation(); handleRevert(zoom); }} title="Восстановить оригинал">↩</button>
            </div>
          )}
        </div>,
        document.body
      )}

      {supplierZoom && (() => {
        const { suppliers, idx } = supplierZoom;
        const zs = suppliers[idx];
        if (!zs) return null;
        const canPrev = idx > 0;
        const canNext = idx < suppliers.length - 1;
        const zSizes = zs.sizes ? zs.sizes.split(',').map(x => x.trim()).filter(Boolean) : [];
        return ReactDOM.createPortal(
          <div className="photo-lightbox photo-lightbox--supplier" onClick={() => setSupplierZoom(null)}>
            <button className="lightbox-close" onClick={e => { e.stopPropagation(); setSupplierZoom(null); }}>×</button>
            {canPrev && (
              <button className="lightbox-arrow lightbox-arrow--left"
                onClick={e => { e.stopPropagation(); setSupplierZoom(z => ({ ...z, idx: z.idx - 1 })); }}>‹</button>
            )}
            <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
              <img
                src={withBust(zs.supplier_photo_url)}
                alt=""
                className="lightbox-photo"
                referrerPolicy="no-referrer"
              />
              {isAdmin && isOurImage(zs.supplier_photo_url) && (
                <div className="photo-rotate-bar">
                  <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                    onClick={e => { e.stopPropagation(); handleRotate(zs.supplier_photo_url, -90); }} title="Повернуть против часовой">↺</button>
                  <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                    onClick={e => { e.stopPropagation(); handleRotate(zs.supplier_photo_url, 90); }} title="Повернуть по часовой">↻</button>
                  <button className="photo-rotate-btn" disabled={!!rotatingUrl}
                    onClick={e => { e.stopPropagation(); handleRevert(zs.supplier_photo_url); }} title="Восстановить оригинал">↩</button>
                </div>
              )}
              <div className="lightbox-info">
                <div className="lightbox-info-header">
                  <span className="lightbox-supplier-name">{zs.supplier_name}</span>
                  {isAdmin && zs.supplier_code && (
                    <span className="lightbox-supplier-code">№ {zs.supplier_code}</span>
                  )}
                  {d.name && <span className="lightbox-product-name">{d.name}</span>}
                  <div className="lightbox-info-right">
                    {zs.photo_link && (
                      <a href={zs.photo_link} target="_blank" rel="noopener noreferrer" className="supplier-crm-btn">CRM</a>
                    )}
                    {zs.price && <span className="lightbox-price">{formatPrice(zs.price)} ₴</span>}
                  </div>
                </div>
                {zSizes.length > 0 && (
                  <div className="supplier-sizes lightbox-sizes">
                    {zSizes.map(sz => (
                      <span key={sz} className="supplier-size-chip">{sz}</span>
                    ))}
                  </div>
                )}
                {zs.material && <div className="supplier-grid lightbox-grid">{zs.material}</div>}
                <div className="supplier-links lightbox-links">
                  <SupplierLink href={zs.supplier_link} label="Поставщик" />
                  <SupplierLink href={zs.photo_post_link} label="Пост с фото" />
                </div>
                {suppliers.length > 1 && (
                  <div className="lightbox-counter">{idx + 1} / {suppliers.length}</div>
                )}
              </div>
            </div>
            {canNext && (
              <button className="lightbox-arrow lightbox-arrow--right"
                onClick={e => { e.stopPropagation(); setSupplierZoom(z => ({ ...z, idx: z.idx + 1 })); }}>›</button>
            )}
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default ClothingProductModal;
